/**
 * ProShip Direct v2 — rate card.
 *
 * The one setup dimension with no ProShip default behind it. ProShip's rates are ProShip's cost,
 * not the merchant's, so standing them in would put a number on the cost comparison that is not
 * the merchant's number — worse than showing no number at all. A missing card therefore leaves
 * Direct cost blank and takes the account out of cheapest-first ranking, and the page says so
 * instead of quietly filling the gap.
 *
 * When a card IS uploaded, the table shows the merchant's own slabs and rates, read back from what
 * they sent. An earlier build showed ProShip's numbers under the heading "your rates are in use
 * for ranking", which was the screen asserting something untrue about the merchant's own pricing.
 */
import React, { useState } from 'react';
import {
  Box, Grid, Typography, Button, Alert, Chip, Tooltip, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import withLayout from '../../../hocs/Layout';
import SetupPageShell from '../components/SetupPageShell';
import FileDropZone from '../components/FileDropZone';
import { SETUP_DIMENSIONS } from '../data/defaults';
import { useRateCard } from '../data/hooks';
import {
  RATE_ZONES, RATE_CSV_COLUMNS, RATE_CSV_TEMPLATE, RATE_ROW_CAP, parseRateCsv, rateSlabsToCsv,
} from '../data/rateCardCsv';
import { downloadCsv } from '../data/serviceabilityUpload';
import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { tHeadSx, tCellSx, statCardSx, statLabelSx, statValueSx, statSubSx } from '../components/stats';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, ERROR } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { MONO } from '../components/directCellStyles';

const dimension = SETUP_DIMENSIONS.find((d) => d.key === 'rateCard');

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** The merchant's own slabs and rates, exactly as uploaded. */
function RateTable({ slabs }) {
  return (
    <Box sx={{ overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: RADIUS.md }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tHeadSx, minWidth: 160 }}>Weight slab</TableCell>
            {RATE_ZONES.map((zone) => (
              <TableCell key={zone} sx={{ ...tHeadSx, minWidth: 90 }}>
                {zone}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {slabs.map((row) => (
            <TableRow key={row.slab}>
              <TableCell sx={{ ...tCellSx, minWidth: 160 }}>{row.slab}</TableCell>
              {row.rates.map((rate, i) => (
                <TableCell
                  key={RATE_ZONES[i]}
                  sx={{ ...tCellSx, minWidth: 90, fontVariantNumeric: 'tabular-nums' }}
                >
                  {inr(rate)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function RateCardUpload({ account, provided, setProvided }) {
  const { card, save, clear } = useRateCard(account.id);
  const [pending, setPending] = useState(null);
  const [fileError, setFileError] = useState(null);

  const onFile = (picked) => {
    setFileError(null);
    setPending(null);

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseRateCsv(reader.result);
      // A file that is unusable as a file gets the banner. Rejected rows go to the review panel,
      // where the per-line reasons are the thing the merchant can act on.
      if (!parsed.slabs.length && !parsed.errors.length) {
        setFileError(parsed.error || 'No rates were found in that file.');
      } else {
        setPending({ ...parsed, fileName: picked.name });
      }
    };
    reader.onerror = () => setFileError('That file could not be read.');
    reader.readAsText(picked);
  };

  const commit = () => {
    save(pending.slabs, pending.summary, pending.fileName);
    setProvided(true);
    setPending(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
      <Box sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography sx={sectionTitleSx}>Your card stays yours</Typography>
          {card && (
            <Chip
              size='small'
              label={`${card.summary.slabCount} slabs`}
              sx={{
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                fontWeight: WEIGHT.semibold,
                height: 20,
                bgcolor: `${SUCCESS}14`,
                color: SUCCESS,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, mb: 1.5 }}>
          <LockOutlinedIcon sx={{ fontSize: 15, color: SUBTLE, mt: '2px' }} />
          <Typography sx={bodySx}>
            Pricing data is encrypted in the database and used to price and rank your shipments, and
            for your dashboard.
          </Typography>
        </Box>

        <Typography sx={{ ...bodySx, mb: 1.5 }}>
          Six columns —{' '}
          <Box component='span' sx={{ fontFamily: MONO, fontSize: SIZE['3xs'], color: NAVY }}>
            {RATE_CSV_COLUMNS.join(', ')}
          </Box>{' '}
          — one row per weight slab. Use your contract&rsquo;s own slabs; they do not have to match
          the template&rsquo;s.
        </Typography>

        <Box sx={{ display: 'flex', gap: GAP.md, mb: 1.5, flexWrap: 'wrap' }}>
          <Button
            size='small'
            startIcon={<GetAppIcon sx={{ fontSize: 16 }} />}
            sx={secondaryBtnSx}
            onClick={() =>
              card
                ? downloadCsv(`rate-card-${account.accountCode}.csv`, rateSlabsToCsv(card.slabs))
                : downloadCsv('rate-card-template.csv', RATE_CSV_TEMPLATE)
            }
          >
            {card ? 'Download current card' : 'Download template'}
          </Button>
        </Box>

        {fileError && (
          <Alert severity='error' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            {fileError}
          </Alert>
        )}

        {!pending && (
          <FileDropZone
            onFile={onFile}
            hint={
              card
                ? 'A new card replaces the one saved for this account. Versioned — the card in force when an order was created stays attached to it.'
                : 'Versioned on upload: the card in force when an order was created stays attached to that order.'
            }
          />
        )}

        {/* ── What is in the file, before it is saved ── */}
        {pending && (
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, p: 1.5 }}>
            <Typography sx={{ ...strongSx, mb: 1, color: pending.slabs.length ? NAVY : ERROR }}>
              {pending.fileName} —{' '}
              {pending.slabs.length
                ? `${pending.slabs.length} slab${pending.slabs.length === 1 ? '' : 's'} read`
                : 'no usable rates'}
              {pending.errors.length > 0 && `, ${pending.errors.length} rejected`}
            </Typography>

            {pending.slabs.length > 0 && (
              <>
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={6} md={3}>
                    <Box sx={statCardSx}>
                      <Typography sx={statLabelSx}>Weight slabs</Typography>
                      <Typography sx={statValueSx}>{pending.summary.slabCount}</Typography>
                      <Typography sx={statSubSx}>across {RATE_ZONES.length} zones</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={statCardSx}>
                      <Typography sx={statLabelSx}>Lowest rate</Typography>
                      <Typography sx={statValueSx}>{inr(pending.summary.lowest)}</Typography>
                      <Typography sx={statSubSx}>in this card</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={statCardSx}>
                      <Typography sx={statLabelSx}>Highest rate</Typography>
                      <Typography sx={statValueSx}>{inr(pending.summary.highest)}</Typography>
                      <Typography sx={statSubSx}>in this card</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={statCardSx}>
                      <Typography sx={statLabelSx}>Rejected</Typography>
                      <Typography sx={{ ...statValueSx, color: pending.errors.length ? ERROR : NAVY }}>
                        {pending.errors.length}
                      </Typography>
                      <Typography sx={statSubSx}>rows not read</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 1.5 }}>
                  <RateTable slabs={pending.slabs} />
                </Box>
              </>
            )}

            {pending.errors.length > 0 && (
              <Box sx={{ bgcolor: LIGHT_BG, borderRadius: RADIUS.md, p: 1.25, mb: 1.5 }}>
                <Typography sx={{ ...strongSx, color: ERROR, mb: 0.5 }}>
                  These rows were not read
                </Typography>
                {pending.errors.slice(0, 8).map((row) => (
                  <Typography key={row.line} sx={smallSx}>
                    Line {row.line}: {row.message}
                  </Typography>
                ))}
                {pending.errors.length > 8 && (
                  <Typography sx={{ ...smallSx, mt: 0.4 }}>
                    …and {pending.errors.length - 8} more.
                  </Typography>
                )}
              </Box>
            )}

            {pending.summary?.truncated > 0 && (
              <Alert severity='info' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                All {pending.slabs.length} slabs were checked, but this build keeps the first{' '}
                {RATE_ROW_CAP}.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: GAP.md, flexWrap: 'wrap' }}>
              <Button
                variant='contained'
                size='small'
                sx={primaryBtnSx}
                disabled={!pending.slabs.length}
                onClick={commit}
              >
                Save rate card
              </Button>
              <Button size='small' sx={secondaryBtnSx} onClick={() => setPending(null)}>
                Discard
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── The card in force, or the consequence of not having one ── */}
      {card && !pending ? (
        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={sectionTitleSx}>Your rates, in use for ranking</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={smallSx}>
              {card.fileName} · uploaded {formatDate(card.uploadedAt, 'dd MMM yyyy')} by{' '}
              {card.uploadedBy}
            </Typography>
            <Tooltip title='Remove this card — Direct cost and the ProShip comparison go blank for this account'>
              <IconButton size='small' onClick={clear} aria-label='remove rate card'>
                <DeleteOutlineIcon sx={{ fontSize: 17, color: SUBTLE }} />
              </IconButton>
            </Tooltip>
          </Box>
          <RateTable slabs={card.slabs} />
        </Box>
      ) : (
        !pending && (
          <Box sx={sectionCardSx}>
            <Typography sx={sectionTitleSx}>No rate card yet</Typography>
            <Typography sx={{ ...bodySx, mt: 0.5 }}>
              ProShip does not hold your rates, and will not substitute its own. Until you upload a card
              for {account.carrierName} · {account.accountCode}:
            </Typography>
            <Box component='ul' sx={{ pl: 2.5, my: 1, '& li': bodySx }}>
              <li>Direct cost is blank for this account, on the dashboard and in reports.</li>
              <li>The Direct vs ProShip cost comparison excludes it.</li>
              <li>Cheapest-first allocation skips it — rules that rank on price will not pick it.</li>
            </Box>
            <Typography sx={bodySx}>
              Everything else still works: the account ships, tracks and invoices normally.
            </Typography>
          </Box>
        )
      )}

      {/*
        The shell's "Revert to ProShip default" flips config.rateCard without knowing this card
        exists. It should not be reachable for a required dimension, but if it is, say so.
      */}
      {card && !provided && !pending && (
        <Alert
          severity='warning'
          sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}
          action={
            <Button size='small' sx={secondaryBtnSx} onClick={clear}>
              Remove card
            </Button>
          }
        >
          This account is marked as having no rate card of its own, but your uploaded card is still
          stored. Remove it, or switch back to your own rates above.
        </Alert>
      )}
    </Box>
  );
}

export function RateCard({ embedded = false }) {
  return (
    <SetupPageShell dimension={dimension} embedded={embedded}>
      {({ account, provided, setProvided }) => (
        <RateCardUpload account={account} provided={provided} setProvided={setProvided} />
      )}
    </SetupPageShell>
  );
}

export default withLayout(RateCard);
